<script>
  import 'mapbox-gl/dist/mapbox-gl.css';
  import mapboxgl from 'mapbox-gl';
  import { onMount, onDestroy, tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  import { getGroupStore } from '@stores/group';
  let recObjectsStore = getGroupStore('recObjectsStore');

  import DetailViewModal from '@app-components/rec_object/detail_view/DetailViewModal.svelte';
  import MapDetailCard from '@app-components/group/MapDetailCard.svelte';

  import { getMapViewport, getGeodata, defaultZoom, defaultCenter } from '@app-components-helper/group/map';

  import queryString from 'query-string';

  mapboxgl.accessToken = import.meta.env['VITE_MAPBOX_API_TOKEN'];

  export let withFooter = true;
  export let searchMode = false;
  export let customClickHandler;
  export let setViewportAutomatically = false;

  let recObjects = [];

  $: recObjectsWithLocation = recObjects.filter((recObject) => getGeodata(recObject).location);

  $: noMapItems = !(recObjectsWithLocation && recObjectsWithLocation.length);

  let showDetailViewModal = false;
  let detailCardRecObjectId;

  let map;
  let recObjectIdsToMarkerEls = {};
  let markerToRecObjectMapping = {};
  let clickedMarkerId;
  let mapViewport = {};

  const showDetailCard = (recObjectId) => {
    detailCardRecObjectId = recObjectId;
  };

  const removeDetailCard = () => {
    detailCardRecObjectId = undefined;
  };

  const markerClicked = (elementOrId) => {
    let element = elementOrId;
    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    if (clickedMarkerId) {
      setDefaultMarkerValues(clickedMarkerId);
    }

    setClickedMarkerValues(element);
    clickedMarkerId = element.id;

    const recObjectId = markerToRecObjectMapping[element.id];
    showDetailCard(recObjectId);
  };

  const markerUnclicked = (elementOrId) => {
    let element = elementOrId;
    if (typeof elementOrId === 'string') {
      element = document.querySelector(`#${elementOrId}`);
    }

    setDefaultMarkerValues(element);

    removeDetailCard();
  };

  const initMap = () => {
    // only init map once
    if (map) {
      return;
    }

    // find viewport options
    let mapViewportOptions = {
      center: [defaultCenter.lng, defaultCenter.lat],
      zoom: defaultZoom,
    };

    const { southwest, northeast } = getMapViewport(recObjectsWithLocation);
    if (southwest) {
      mapViewportOptions = { bounds: [southwest, northeast] };
    }

    // combine with default options and init map.
    map = new mapboxgl.Map({
      ...{
        container: 'm-map-view__map', // container ID
        style: 'mapbox://styles/mapbox/light-v10', // style URL
      },
      ...mapViewportOptions,
    });

    // disable map rotation using right click + drag
    map.dragRotate.disable();
    // disable map rotation using touch rotation gesture
    map.touchZoomRotate.disableRotation();
    // disable map pitch adjustment with two finger slide
    map.touchPitch.disable();

    // Unhighlight any markers when the map is clicked outside a marker.
    map.on('click', (e) => {
      const { clientX, clientY } = e.originalEvent;
      const intersectedMarkers = document
        .elementsFromPoint(clientX, clientY)
        .filter((el) => [...el.classList].includes('marker'));

      if (intersectedMarkers.length) {
        return;
      }

      if (clickedMarkerId) {
        markerUnclicked(clickedMarkerId);
        clickedMarkerId = undefined;
      }
    });

    dispatch('init-map', map);
  };

  const setMapViewportBasedOnRecObjects = () => {
    // update component variable
    mapViewport = getMapViewport(recObjectsWithLocation);

    // set on map if map exists
    const { southwest, northeast } = mapViewport;

    if (map && southwest && northeast) {
      map.fitBounds([southwest, northeast]);
    }
  };

  const setDefaultMarkerValues = (elementOrId) => {
    let el = elementOrId;
    if (typeof elementOrId === 'string') {
      el = document.querySelector(`#${elementOrId}`);
    }

    el.classList.remove('marker-clicked');
    el.classList.add('marker-default');
    el.src = '/images/map/marker.svg';
    el.style.width = '27px';
    el.style.height = '41px';
  };

  const setClickedMarkerValues = (elementOrId) => {
    let el = elementOrId;
    if (typeof elementOrId === 'string') {
      el = document.querySelector(`#${elementOrId}`);
    }

    el.classList.remove('marker-default');
    el.classList.add('marker-clicked');
    el.src = '/images/map/marker-black.svg';
    el.style.width = '27px';
    el.style.height = '41px';
  };

  const redrawMarkers = () => {
    // draw new markers:
    recObjectsWithLocation.forEach((recObject) => {
      if (document.getElementById(`map-marker-for-${recObject.id}`)) {
        return;
      }

      const { location } = getGeodata(recObject);

      const el = document.createElement('img');
      el.id = `map-marker-for-${recObject.id}`;
      el.alt = '[map marker]';
      el.classList.add('marker');
      setDefaultMarkerValues(el);

      el.addEventListener('click', (e) => {
        markerClicked(e.currentTarget);
      });

      markerToRecObjectMapping[el.id] = recObject.id;
      recObjectIdsToMarkerEls[recObject.id] = el;

      new mapboxgl.Marker({ element: el }).setLngLat([location.lng, location.lat]).addTo(map);
    });

    // if the previously "clicked" marker no longer exists, make sure it isn't set
    // as the "clicked" marker in memory.
    if (clickedMarkerId && !document.querySelector(`#${clickedMarkerId}`)) {
      clickedMarkerId = undefined;
    }
  };

  const unsubRecObjects = recObjectsStore.subscribe(async (newRecObjects) => {
    recObjects = newRecObjects;
    if (map) {
      await tick();
      redrawMarkers();

      if (setViewportAutomatically) {
        setMapViewportBasedOnRecObjects();
      }

      if (clickedMarkerId) {
        markerClicked(clickedMarkerId);
      }
    }
  });

  const popStateListener = function (_event) {
    let detailId = queryString.parse(location.search).detail_id;
    showDetailViewModal = detailId && detailCardRecObjectId == detailId;
  };

  onMount(() => {
    initMap();
    redrawMarkers();

    window.addEventListener('popstate', popStateListener);
  });

  onDestroy(() => {
    unsubRecObjects();

    window.removeEventListener('popstate', popStateListener);
  });
</script>

<!-- NOTE: this is global so map can pick it up, so ensure that it's all scoped within the #m-map-view__map selector -->
<style style="text/scss" lang="scss" global>
  #m-map-view__map {
    width: 100%;
    height: 100%;

    .mapbox-improve-map {
      display: none;
    }

    &.no-map-items {
      &:after {
        content: ' ';
        z-index: 3;
        display: block;
        position: absolute;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.5);
      }

      .m-map-view__no-items-text {
        position: absolute;
        z-index: 4;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .marker {
      cursor: pointer;
    }
  }
</style>

<div class="m-map-view__map" class:no-map-items={noMapItems} id="m-map-view__map">
  {#if noMapItems}
    <div class="m-map-view__no-items-text">
      {'Add locations to any recommendations to make them appear on the map'}
    </div>
  {/if}

  {#if detailCardRecObjectId}
    <MapDetailCard
      withFooter={withFooter}
      recObjectId={detailCardRecObjectId}
      recObjectsWithLocation={recObjectsWithLocation}
      mobile={true}
      on:click={() => {
        if (customClickHandler) {
          customClickHandler(detailCardRecObjectId);
        } else {
          showDetailViewModal = true;
        }
      }} />
  {/if}

  {#if detailCardRecObjectId && showDetailViewModal}
    <DetailViewModal
      mobile={true}
      showActionBar={searchMode ? false : true}
      showShareCard={true}
      recObjectId={detailCardRecObjectId}
      bind:showModal={showDetailViewModal} />
  {/if}
</div>
